import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Card, Button, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getJobs } from '../../services/jobService';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './JobCalendar.css'; 


import { formatJobNumber } from '../../utils/jobUtils';

/**
 * JobCalendar Component
 * Displays scheduled jobs in a calendar view with enhanced sidebar for selected date
 */
const JobCalendar = ({ region, onSelectDate, hideScheduleButton = false }) => {
  // State management
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [selectedDateJobs, setSelectedDateJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarView, setCalendarView] = useState('week');
  
  const navigate = useNavigate();
  const localizer = momentLocalizer(moment);
  
  /**
   * Format date to consistent string format for comparison
   * @param {Date} date - The date to format
   * @returns {string} Formatted date string (YYYY-MM-DD)
   */
  const formatDateKey = (date) => {
    return moment(date).format('YYYY-MM-DD');
  };
  
  /**
   * Get display name for customer, handling empty/temp values
   * @param {Object} job - Job object
   * @returns {string} Formatted customer name
   */
  const getDisplayCustomerName = (job) => {
    if (!job.customer_name || job.customer_name.toLowerCase() === 'temp' || job.customer_name.trim() === '') {
      return `Customer #${formatJobNumber(job.job_number) || 'Unknown'}`;
    }
    return job.customer_name;
  };
  
  /**
   * Get display address for job, handling empty values
   * @param {Object} job - Job object
   * @returns {string} Formatted address or fallback
   */
  const getDisplayAddress = (job) => {
    if (!job.address || job.address.trim() === '') {
      return 'Address not specified';
    }
    // Get first line of address for cleaner display
    const addressParts = job.address.split(',');
    return addressParts[0].trim();
  };
  
  const loadScheduledJobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { status: 'scheduled' };
      if (region && region !== 'ALL') {
        params.region = region;
      }
      
      const fetchedJobs = await getJobs(params);
      
      // Filter and Map in one pass using reduce
      const jobEvents = fetchedJobs.reduce((events, job) => {
        // First, check if a scheduled_date even exists and is not an empty string
        if (!job.scheduled_date) {
          return events;
        }

        // Attempt to parse the date and move it to noon to avoid timezone issues
        const startMoment = moment(job.scheduled_date, ['YYYY-MM-DD', 'MM/DD/YYYY'])
                              .startOf('day')
                              .add(12, 'hours');

        // Check if the parsed date is valid
        if (startMoment.isValid()) {
          const startDate = startMoment.toDate();
          const endDate = moment(startMoment).add(1, 'hour').toDate();

          events.push({
            id: job.id,
            title: `${getDisplayCustomerName(job)}: ${(job.address || '').substring(0, 15)}${(job.address || '').length > 15 ? '...' : ''}`,
            start: startDate,
            end: endDate,
            resource: job
          });
        } else {
          console.warn(
            `Skipping job due to malformed scheduled_date: '${job.scheduled_date}'`,
            job
          );
        }

        return events;
      }, []);

      setJobs(jobEvents);
      updateSelectedDateJobs(calendarDate, jobEvents);
    } catch (error) {
      console.error('Error loading scheduled jobs:', error);
      setError('Failed to load jobs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [region]);
  
  // Load jobs when region changes
  useEffect(() => {
    loadScheduledJobs();
  }, [region, loadScheduledJobs]);
  
  /**
   * Update the selected date jobs whenever date or jobs change
   * @param {Date} date - The selected date
   * @param {Array} jobsList - List of jobs to filter (defaults to current jobs state)
   */
  const updateSelectedDateJobs = useCallback((date, jobsList = jobs) => {
    const dateStr = formatDateKey(date);
    const filtered = jobsList.filter(job => {
      const jobDateStr = formatDateKey(job.start);
      return jobDateStr === dateStr;
    }).map(job => job.resource);
    
    setSelectedDateJobs(filtered);
  }, [jobs]);
  
  /**
   * Handle date selection in the calendar
   * @param {Object} slotInfo - Information about the selected slot
   */
  const handleSelectSlot = ({ start }) => {
    const selectedDate = new Date(start);
    selectedDate.setHours(12, 0, 0, 0);
    
    setCalendarDate(selectedDate);
    updateSelectedDateJobs(selectedDate);
    
    if (onSelectDate) {
      onSelectDate(selectedDate);
    }
  };
  
  /**
   * Handle clicking on a job in the calendar
   * Selects the date of the clicked event instead of navigating away
   * @param {Object} event - The calendar event that was clicked
   */
  const handleSelectEvent = (event) => {
    const selectedDate = new Date(event.start);
    selectedDate.setHours(12, 0, 0, 0);

    setCalendarDate(selectedDate);
    updateSelectedDateJobs(selectedDate);

    if (onSelectDate) {
      onSelectDate(selectedDate);
    }
  };
  
  /**
   * Format the displayed date in header
   * @param {Date} date - The date to format
   * @returns {string} Formatted date string
   */
  const formatDisplayDate = (date) => {
    if (!date) return '';
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };
  
  /**
   * Custom toolbar component for the calendar
   */
  const CustomToolbar = (toolbar) => {
    const goToBack = () => toolbar.onNavigate('PREV');
    const goToNext = () => toolbar.onNavigate('NEXT');
    const goToToday = () => toolbar.onNavigate('TODAY');

    const viewLabels = { month: 'Month', week: 'Week', day: 'Day', agenda: 'List' };

    return (
      <div className="jobcal-toolbar d-flex justify-content-between align-items-center mb-2">
        <div>
          <Button
            variant="primary"
            size="sm"
            onClick={goToBack}
            style={{ fontSize: '0.75rem', fontWeight: '500', padding: '0.25rem 0.5rem' }}
          >
            {'<'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={goToToday}
            className="mx-2"
            style={{ fontSize: '0.75rem', fontWeight: '500', padding: '0.25rem 0.6rem' }}
          >
            Today
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={goToNext}
            style={{ fontSize: '0.75rem', fontWeight: '500', padding: '0.25rem 0.5rem' }}
          >
            {'>'}
          </Button>
          <span className="ms-3 fw-bold" style={{ fontSize: '1rem' }}>{toolbar.label}</span>
        </div>
        <div className="btn-group">
          {Object.keys(viewLabels).map(view => (
            <Button
              key={view}
              variant={toolbar.view === view ? "primary" : "outline-secondary"}
              size="sm"
              onClick={() => {
                toolbar.onView(view);
                setCalendarView(view);
              }}
              style={{ fontSize: '0.75rem', fontWeight: '500', padding: '0.3rem 0.6rem' }}
            >
              {viewLabels[view]}
            </Button>
          ))}
        </div>
      </div>
    );
  };
  
  /**
   * Custom styling for calendar events
   */
  const eventStyleGetter = (event) => {
    const status = event.resource?.status?.toLowerCase() || 'scheduled';
    
    let backgroundColor = '#007bff';
    
    switch (status) {
      case 'completed': backgroundColor = '#28a745'; break;
      case 'in_progress': backgroundColor = '#ffc107'; break;
      case 'cancelled': backgroundColor = '#dc3545'; break;
      default: backgroundColor = '#007bff';
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        color: status === 'in_progress' ? '#212529' : '#fff',
        border: 'none',
        display: 'block',
        fontSize: '0.85rem',
        padding: '2px 4px'
      }
    };
  };
  
  /**
   * Custom day cell component for month view
   */
  const DateCellWrapper = ({ value }) => {
  const date = value;

  return (
    <div className="rbc-day-cell-custom">
      <div className="rbc-day-number-centered">
        {date.getDate()}
      </div>
    </div>
  );
};

  
  /**
   * Component to display job status with appropriate styling
   */
  const JobStatusBadge = ({ status }) => {
    let variant = 'primary';
    
    switch (status?.toLowerCase()) {
      case 'completed': variant = 'success'; break;
      case 'in_progress': variant = 'warning'; break;
      case 'cancelled': variant = 'danger'; break;
      default: variant = 'primary';
    }
    
    return (
      <Badge bg={variant} className="status-badge">
        {status ? status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1) : 'Scheduled'}
      </Badge>
    );
  };
  
  /**
   * Enhanced empty state component
   */
  const EmptyJobsState = () => (
    <div className="empty-jobs-state">
      <span className="empty-jobs-icon">📅</span>
      <div className="empty-jobs-text">No jobs scheduled</div>
      <div className="empty-jobs-subtext">Select a different date or schedule a new job</div>
    </div>
  );
  
  if (loading && jobs.length === 0) {
    return (
      <div className="text-center p-3">
        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span>Loading jobs...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <Button variant="outline-danger" size="sm" className="ms-2" onClick={() => loadScheduledJobs()}>Retry</Button>
      </div>
    );
  }
  
  // Custom formats for calendar display
  const calendarFormats = {
    weekdayFormat: (date, culture, localizer) => {
      // On mobile, show single letter; on desktop, show abbreviated
      if (window.innerWidth <= 768) {
        return localizer.format(date, 'dd', culture).charAt(0);
      }
      return localizer.format(date, 'ddd', culture);
    },
    dayFormat: 'D',
    monthHeaderFormat: 'MMMM YYYY',
    dayHeaderFormat: 'dddd, MMMM D',
    dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'MMM D', culture)} - ${localizer.format(end, 'MMM D', culture)}`,
  };

  /**
   * Get the week days starting from the current calendar date
   */
  const getWeekDays = () => {
    const startOfWeek = moment(calendarDate).startOf('week');
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.clone().add(i, 'days').toDate());
    }
    return days;
  };

  /**
   * Get jobs for a specific date, grouped by region
   */
  const getJobsForDate = (date) => {
    const dateStr = formatDateKey(date);
    const dayJobs = jobs.filter(job => formatDateKey(job.start) === dateStr).map(job => job.resource);

    // Group by region
    const grouped = dayJobs.reduce((acc, job) => {
      const region = job.region || 'Other';
      if (!acc[region]) acc[region] = [];
      acc[region].push(job);
      return acc;
    }, {});

    // Sort regions: OC first, then LA, then IE, then Other
    const regionOrder = ['OC', 'LA', 'IE', 'Other'];
    const sortedRegions = Object.keys(grouped).sort((a, b) => {
      return regionOrder.indexOf(a) - regionOrder.indexOf(b);
    });

    return { grouped, sortedRegions, total: dayJobs.length };
  };

  /**
   * Navigate week
   */
  const navigateWeek = (direction) => {
    const newDate = moment(calendarDate).add(direction === 'NEXT' ? 7 : -7, 'days').toDate();
    setCalendarDate(newDate);
    updateSelectedDateJobs(newDate, jobs);
  };

  const goToToday = () => {
    const today = new Date();
    setCalendarDate(today);
    updateSelectedDateJobs(today, jobs);
  };

  /**
   * Format week range for header
   */
  const getWeekRangeLabel = () => {
    const startOfWeek = moment(calendarDate).startOf('week');
    const endOfWeek = moment(calendarDate).endOf('week');
    return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D')}`;
  };

  /**
   * Render custom condensed week view
   */
  /**
   * Get region display name
   */
  const getRegionDisplayName = (regionCode) => {
    const names = { 'OC': 'Orange County', 'LA': 'Los Angeles', 'IE': 'Inland Empire', 'Other': 'Other' };
    return names[regionCode] || regionCode;
  };

  const renderCondensedWeekView = () => {
    const weekDays = getWeekDays();
    const today = formatDateKey(new Date());

    return (
      <div className="condensed-week-view">
        {/* Week Navigation */}
        <div className="condensed-week-toolbar d-flex justify-content-between align-items-center mb-3">
          <div>
            <Button variant="primary" size="sm" onClick={() => navigateWeek('PREV')}>{'<'}</Button>
            <Button variant="primary" size="sm" onClick={goToToday} className="mx-2">Today</Button>
            <Button variant="primary" size="sm" onClick={() => navigateWeek('NEXT')}>{'>'}</Button>
            <span className="ms-3 fw-bold">{getWeekRangeLabel()}</span>
          </div>
          <div className="btn-group">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setCalendarView('month')}
            >
              Month
            </Button>
            <Button
              variant="primary"
              size="sm"
            >
              Week
            </Button>
          </div>
        </div>

        {/* Week Grid */}
        <div className="condensed-week-grid">
          {weekDays.map((day, index) => {
            const { grouped, sortedRegions, total } = getJobsForDate(day);
            const isToday = formatDateKey(day) === today;
            const isSelected = formatDateKey(day) === formatDateKey(calendarDate);

            return (
              <div
                key={index}
                className={`condensed-week-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  setCalendarDate(day);
                  updateSelectedDateJobs(day, jobs);
                  if (onSelectDate) onSelectDate(day);
                }}
              >
                <div className="condensed-week-day-header">
                  <div className="day-name">{moment(day).format('ddd')}</div>
                  <div className="day-date">{moment(day).format('MMM D')}</div>
                  <div className="job-count">{total} job{total !== 1 ? 's' : ''}</div>
                </div>
                <div className="condensed-week-day-jobs">
                  {total === 0 ? (
                    <div className="no-jobs-text">No jobs</div>
                  ) : (
                    sortedRegions.map(region => (
                      <React.Fragment key={region}>
                        {grouped[region].map(job => (
                          <div
                            key={job.id}
                            className="condensed-job-card"
                          >
                            <div className="condensed-job-header">
                              <span className="condensed-job-number">{formatJobNumber(job.job_number)}</span>
                              <JobStatusBadge status={job.status} />
                            </div>
                            <div className="condensed-job-customer">{getDisplayCustomerName(job)}</div>
                            <div className="condensed-job-address">{getDisplayAddress(job)}</div>
                            <div className="condensed-job-region">{job.region}</div>
                          </div>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="job-calendar-container">
      <div className="calendar-section">
        <h3 className="calendar-title mb-3 text-center">Job Schedule</h3>
        <Card className="shadow-sm mb-0">
          <Card.Body className="p-3">
            {calendarView === 'week' ? (
              renderCondensedWeekView()
            ) : (
              <Calendar
                localizer={localizer}
                events={jobs}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '72vh', minHeight: 550 }}
                defaultView="month"
                view={calendarView}
                views={['month', 'day', 'agenda']}
                formats={calendarFormats}
                selectable
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
                onNavigate={date => {
                  setCalendarDate(date);
                  updateSelectedDateJobs(date, jobs);
                }}
                onView={view => setCalendarView(view)}
                popup
                tooltipAccessor={event =>
                  `${event.resource?.job_number}\n` +
                  `${getDisplayCustomerName(event.resource)}\n` +
                  `Status: ${event.resource?.status || 'Scheduled'}`
                }
                components={{
                  toolbar: (props) => (
                    <div className="jobcal-toolbar d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <Button variant="primary" size="sm" onClick={() => props.onNavigate('PREV')}>{'<'}</Button>
                        <Button variant="primary" size="sm" onClick={() => props.onNavigate('TODAY')} className="mx-2">Today</Button>
                        <Button variant="primary" size="sm" onClick={() => props.onNavigate('NEXT')}>{'>'}</Button>
                        <span className="ms-3 fw-bold">{props.label}</span>
                      </div>
                      <div className="btn-group">
                        <Button
                          variant={calendarView === 'month' ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setCalendarView('month')}
                        >
                          Month
                        </Button>
                        <Button
                          variant={calendarView === 'week' ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setCalendarView('week')}
                        >
                          Week
                        </Button>
                      </div>
                    </div>
                  )
                }}
              />
            )}
          </Card.Body>
        </Card>
      </div>

      {calendarView !== 'week' && (
        <div className="jobs-sidebar">
          <Card className="shadow-sm">
            <Card.Header className="py-3">
              <h6 className="mb-1 fw-bold">{formatDisplayDate(calendarDate)}</h6>
              <small>
                {selectedDateJobs.length} job{selectedDateJobs.length === 1 ? '' : 's'} scheduled
              </small>
            </Card.Header>
            <Card.Body className="p-0">
              {selectedDateJobs.length === 0 ? (
                <EmptyJobsState />
              ) : (
                <div className="jobs-list">
                  {selectedDateJobs.map(job => (
                    <div
                      key={job.id}
                      className="job-item cursor-pointer"
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      <div className="job-info">
                        <div className="job-header">
                          <div className="job-number">{formatJobNumber(job.job_number)}</div>
                          <JobStatusBadge status={job.status} />
                        </div>
                        <div className="customer-name">{getDisplayCustomerName(job)}</div>
                        <div className="job-address">
                          {getDisplayAddress(job)}
                        </div>
                        {job.region && (
                          <div className="job-region">
                            Market: {job.region}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {onSelectDate && !hideScheduleButton && (
                <div className="schedule-button-container">
                  <Button
                    variant="success"
                    size="sm"
                    className="w-100"
                    onClick={() => onSelectDate(calendarDate)}
                  >
                    📅 Schedule New Job
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};

export default JobCalendar;